<template>
<section class="charts-wrapper">
  <section class="normal-wrapper">
    <e-chart ref="normal-chart" />
  </section>

  <section class="related-wrapper">
    <div class="related-header">
      <span>{{factors[0].displayName}}</span>
      <span style="padding:0 .5em;">VS</span>
      <span>{{factors[1].displayName}}</span>
    </div>

    <related-chart
      :base-data="relatedData.x"
      :related-data="relatedData.y"
      :audit-rule-map="auditRuleMap"
      @dblclick="onRelatedChartDBLClick"
    />
  </section>
</section>
</template>

<script>
import {AXIS_LABEL, AXIS_LINE_STYLE, NUMBER_FONT_FAMILY, TEXT_FONT_FAMILY} from './FactorCharts/chartStyle'

export default {
  name: 'AuditFactorRelatedChart',
  props: {
    data: {
      required: true,
      type: Object
    },
    factors: {
      required: true,
      type: Array
    },
    unitProp: {
      required: true,
      type: String
    },
    auditRuleMap: {
      required: true,
      type: Object
    }
  },
  data () {
    return {
      relatedData: Object.freeze({
        x: {
          data: []
        },
        y: {
          data: []
        }
      })
    }
  },
  methods: {
    render () {
      this.renderNormalChart()
      this.renderRelatedChart()
    },
    renderNormalChart () {
      let {data, factors} = this
      let dataList = data.data
      let dataTimes = dataList.map(data => new Date(data.ts).format('YY年MM月DD日HH时'))
      // let ratio = Math.min(50, 50 / dataList.length * 100)
      let chartOpt = {
        title: {
          text: data.name,
          left: 'center',
          textStyle: {
            fontFamily: TEXT_FONT_FAMILY
          }
        },
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          right: 23
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true
        },
        legend: {
          top: 27,
          left: 'center',
          width: '75%',
          type: 'scroll'
        },
        grid: [{
          top: 75,
          right: 40,
          bottom: 45,
          left: 30,
          containLabel: true
        }],
        xAxis: [{
          type: 'category',
          data: dataTimes,
          axisLine: {
            show: true,
            lineStyle: AXIS_LINE_STYLE
          },
          axisLabel: {
            ...AXIS_LABEL,
            fontFamily: TEXT_FONT_FAMILY
          },
          axisTick: {
            show: true,
            alignWithLabel: true
          }
        }],
        yAxis: [{
          name: this.factors[0][this.unitProp],
          type: 'value',
          nameGap: 10,
          // nameLocation: 'center',
          axisLine: {
            show: true,
            lineStyle: AXIS_LINE_STYLE
          },
          // splitLine: {
          //   show: index === 0
          // },
          axisLabel: {
            ...AXIS_LABEL,
            fontFamily: NUMBER_FONT_FAMILY
          }
        }],
        dataZoom: [{
          type: 'inside',
          start: dataTimes.length <= 72 ? 0 : ((dataTimes.length - 72) / dataTimes.length * 100).toFixed(0),
          end: 100,
          realtime: true,
          xAxisIndex: 0,
          minSpan: 5
        }, {
          type: 'slider',
          start: dataTimes.length <= 72 ? 0 : ((dataTimes.length - 72) / dataTimes.length * 100).toFixed(0),
          end: 100,
          realtime: true,
          xAxisIndex: 0,
          minSpan: 5,
          // height: 39,
          bottom: 9,
          left: 'center',
          handleIcon: 'M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '70%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 4,
            shadowColor: 'rgba(0, 0, 0, 0.15)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          },
          // dataBackground: {
          //   areaStyle: '#E4F0FF'
          // }
          fillerColor: 'rgba(228, 240, 255, .6)',
          borderColor: '#E4F0FF'
        }],
        series: factors.map(({id, name}) => ({
          id,
          name,
          type: 'line',
          showSymbol: false,
          data: dataList.map(data => data[id])
        }))
      }

      this.$refs['normal-chart'].setOpt(chartOpt)
    },
    renderRelatedChart () {
      let {data, unitProp} = this
      let dataList = data.data
      let factors = this.factors.map(factor => ({
        id: factor.id,
        name: factor.displayName,
        unit: factor[unitProp]
      }))
      this.relatedData = Object.freeze({
        x: this.getValues(factors[0], dataList),
        y: this.getValues(factors[1], dataList)
      })
    },
    getValues (factor, dataList) {
      let factorId = factor.id
      factor.data = dataList.map(data => ({
        dataTime: data.dataTime,
        value: data[factorId],
        flag: data[`${factorId}Flag`]
      }))
      return factor
    },
    onRelatedChartDBLClick (chartData) {
      let {data, auditRuleMap, factors} = this
      let {dataTime} = chartData[4]
      let dataList = data.data
      let index = dataList.findIndex(data => data.dataTime === dataTime)
      let start = Math.max(0, index - 5)
      let end = Math.min(dataList.length, start + 11)
      start = Math.max(0, end - 11)
      this.$emit('dblclick', Object.freeze({
        id: data.id,
        name: data.name,
        dataTime,
        start,
        end,
        dataList: Object.freeze(dataList.slice(start, end).map(data => factors.reduce((map, {id}) => {
          let flag = data[`${id}Flag`]
          map[id] = `${data[id] ? `${data[id]}${flag ? `(${auditRuleMap[flag] ? auditRuleMap[flag].name : '正常'})` : ''}` : ''}`
          return map
        }, {
          dataTime: data.dataTime,
          ts: data.ts
        })))
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.charts-wrapper {
  width: 100%;
  height: 500px;
  min-height: 500px;

  &:after {
    content: "";
    clear: both;
  }

  > * {
    float: left;
    height: 100%;
  }

  > .normal-wrapper {
    width: 58%;
  }

  > .related-wrapper {
    $RELATED-HEADER-HEIGHT: 40px;
    position: relative;
    width: 42%;
    padding-top: 100px;

    > .related-header {
      position: absolute;
      font-size: 22px;
      top: 30px;
      left: 0;
      width: 100%;
      height: $RELATED-HEADER-HEIGHT;
      line-height: $RELATED-HEADER-HEIGHT;
      text-align: center;

      > * {
        vertical-align: middle;
      }
    }

    > .related-selector {
      position: absolute;
      top: $RELATED-HEADER-HEIGHT;
      left: 0;
      width: 100%;
      height: 60px;
      padding: 14px 0;
      text-align: center;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Data/AuditFactorRelatedChart.vue